import React, { memo, useEffect, useMemo, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { ChevronArrowRightIcon } from '../../assets/icons/ChevronArrowRightIcon';
import { CloseIcon } from '../../assets/icons/CloseIcon';
import { SearchIcon } from '../../assets/icons/SearchIcon';
import { SicrediLogo } from '../../assets/icons/SicrediLogo';
import { ThreeDotsLoader } from '../../assets/icons/ThreeDotsLoader';
import { SicrediLogoTheme } from '../../libraries/SicrediLogoThemes';
import { getMergedClassNames } from '../../utils/HTMLutils';
import { getStringWithoutSpecialChar } from '../../utils/MaskUtils';
import Col from '../col/Col';
import Collapse from '../collapse/Collapse';
import IconButton from '../iconButton/IconButton';
import Input from '../input/Input';
import MenuItem, { MenuItemProps } from './components/menuItem/MenuItem';
import './Menu.scss';
const rootClassName = 'component-menu';
let timeout: any;

interface MenuProps {
  loading?: boolean;
  className?: string;
  buscar?: boolean;
  shortOnMD?: boolean;
  openOnClick?: boolean;
  hamburgerMenu?: boolean;
  opened?: boolean;
  setOpened?: (event?: any) => void;
  logo?: React.ReactNode;
  items?: MenuItemProps[];
}

const format = (s: string) =>
  getStringWithoutSpecialChar(s).trim().toLowerCase();
function Menu(props: MenuProps) {
  const [opened, setOpened] = useState(false);
  const [buscarValue, setBuscarValue] = useState('');

  useEffect(() => {
    const closeOnEsc = (evt: any) => {
      if (evt.key === 'Escape') {
        setOpened(false);
        if (typeof props.setOpened === 'function') props.setOpened(false);
      }
    };
    if (opened || props.opened) {
      document.addEventListener('keydown', closeOnEsc);
    }
    return () => {
      document.removeEventListener('keydown', closeOnEsc);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, props.opened]);

  useEffect(() => {
    const onResizeHandle = (evt: any) => {
      if (evt.target.innerWidth > 991.98) {
        setOpened(false);
        if (typeof props.setOpened === 'function') props.setOpened(false);
      }
    };
    if (props.openOnClick) {
      window.addEventListener('resize', onResizeHandle);
    }
    return () => {
      window.removeEventListener('resize', onResizeHandle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.openOnClick]);

  const getProps = () => {
    const openedOrNot = (condition: any) => {
      if (condition) return `${rootClassName}-opened`;
      return '';
    };
    const p: Partial<MenuProps> = {
      ...props,
      className: getMergedClassNames([
        rootClassName,
        'scroll-white',
        props.className,
        props.shortOnMD ? `${rootClassName}-short-on-md` : '',
        props.openOnClick ? `${rootClassName}-open-on-click` : '',
        props.hamburgerMenu ? `${rootClassName}-hamburger-menu` : '',
        (props.openOnClick || props.hamburgerMenu) &&
          (props.opened !== undefined
            ? openedOrNot(props.opened)
            : openedOrNot(opened)),
      ]),
    };
    delete p.loading;
    delete p.openOnClick;
    delete p.shortOnMD;
    delete p.items;
    delete p.opened;
    delete p.setOpened;
    delete p.hamburgerMenu;
    delete p.logo;
    delete p.buscar;
    return p;
  };

  useEffect(() => {
    if (timeout) clearTimeout(timeout);
    if (!opened && !props.opened) {
      timeout = setTimeout(() => {
        setBuscarValue('');
      }, 300);
    }
  }, [opened, props.opened]);

  const itens = useMemo(() => {
    if (!props.items) return [];

    return props.items
      .filter((it) => {
        return (
          !buscarValue ||
          !format(buscarValue) ||
          (it.label && format(it.label).includes(format(buscarValue))) ||
          (it.subList &&
            it.subList.some(
              (subItem) =>
                subItem.label &&
                format(subItem.label).includes(format(buscarValue))
            ))
        );
      })
      .map((it) => {
        return !it.subList || !it.subList.length
          ? it
          : {
              ...it,
              subList: it.subList.filter((subItem) => {
                return (
                  !buscarValue ||
                  !format(buscarValue) ||
                  (it.label &&
                    format(it.label).includes(format(buscarValue))) ||
                  (subItem.label &&
                    format(subItem.label).includes(format(buscarValue)))
                );
              }),
            };
      });
  }, [buscarValue, props.items]);

  return (
    <React.Fragment>
      <div {...getProps()}>
        {props.hamburgerMenu && (
          <IconButton
            className={`${rootClassName}-close-button`}
            icon={<CloseIcon />}
            onClick={() => {
              setOpened(false);
              if (typeof props.setOpened === 'function') props.setOpened(false);
            }}
          />
        )}
        <div className={`${rootClassName}-logo`}>{props.logo}</div>
        <Collapse animateOpacity opened={props.loading}>
          <Col centralized>
            <ThreeDotsLoader size={2} />
          </Col>
        </Collapse>
        <Collapse animateOpacity opened={props.buscar && !props.loading}>
          <div className={`${rootClassName}-buscar`}>
            <Input
              disabled={props.loading || !props.buscar}
              id='buscar-menu'
              placeholder='Buscar'
              value={buscarValue}
              onChange={(evt) => {
                setBuscarValue(evt?.target.value);
              }}
              rightObject={<IconButton icon={<SearchIcon />} />}
            />
          </div>
        </Collapse>
        <Collapse animateOpacity opened={itens.length > 0}>
          <div className={`${rootClassName}-items`}>
            {itens.map((it, index) => {
              return (
                <div className={`${rootClassName}-it`} key={index}>
                  <MenuItem
                    {...it}
                    buscar={props.buscar}
                    buscarValue={buscarValue}
                  />
                </div>
              );
            })}
          </div>
        </Collapse>
      </div>
      <React.Fragment>
        {props.openOnClick && (
          <button
            className={`${rootClassName}-toggle ${
              opened ? `${rootClassName}-opened` : ''
            }`}
            onClick={() => {
              setOpened(!opened);
              if (typeof props.setOpened === 'function') props.setOpened(false);
            }}
          >
            <ChevronArrowRightIcon />
          </button>
        )}
      </React.Fragment>
      <React.Fragment>
        {(props.openOnClick || props.hamburgerMenu) && (
          <CSSTransition
            timeout={300}
            classNames={`${rootClassName}-background`}
            in={
              (props.openOnClick || props.hamburgerMenu) &&
              (props.opened !== undefined ? props.opened : opened)
            }
            mountOnEnter
            unmountOnExit
          >
            <div
              className={`${rootClassName}-background`}
              onClick={() => {
                setOpened(false);
                if (typeof props.setOpened === 'function')
                  props.setOpened(false);
              }}
            />
          </CSSTransition>
        )}
      </React.Fragment>
    </React.Fragment>
  );
}

Menu.defaultProps = {
  openOnClick: false,
  shortOnMD: false,
  hamburgerMenu: false,
  opened: undefined,
  logo: <SicrediLogo theme={SicrediLogoTheme.Verde} height='35px' />,
  items: [],
};

export default memo(Menu);
